import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import StatusBadge from "../components/StatusBadge";
import WorkflowStepper from "../components/WorkflowStepper";
import MediaUpload from "../components/MediaUpload";
import InspectionChat from "../components/InspectionChat";
import { ArrowLeft, CheckCircle2, XCircle, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const WORKFLOW_CHAIN = [
  "consulting_tech_office",
  "consulting_inspector",
  "consulting_topographer",
  "consulting_material_engineer",
  "consulting_estimate_engineer",
  "consulting_team_lead",
];

export default function InspectionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [insp, setInsp] = useState(null);
  const [comment, setComment] = useState("");
  const [media, setMedia] = useState([]);
  const [numericCheck, setNumericCheck] = useState({ verified_cost: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [subForm, setSubForm] = useState({ failed_roles: [], comment: "" });

  const load = () => api.get(`/inspections/${id}`).then((r) => setInsp(r.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!insp) return <div className="mono text-sm">Yüklənir...</div>;

  const idx = insp.current_step_index;
  const currentStep = insp.workflow[idx];
  const isMyTurn =
    (insp.status === "active" || insp.status === "awaiting_team_lead") &&
    currentStep?.user_id === user?.id &&
    currentStep?.status === "pending";
  const isTeamLeadStep = currentStep?.role === "consulting_team_lead";

  const submit = async (decision) => {
    if (!comment.trim()) {
      toast.error(t("comment_required"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        decision,
        comment,
        media,
      };
      if (currentStep.role === "consulting_estimate_engineer") {
        payload.numeric_check = numericCheck;
      }
      await api.post(`/inspections/${id}/action`, payload);
      toast.success("Aksiya tamamlandı");
      setComment("");
      setMedia([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    } finally {
      setSubmitting(false);
    }
  };

  const createSubRequest = async () => {
    if (!subForm.failed_roles.length || !subForm.comment.trim()) {
      toast.error("Ən azı bir rol və şərh seçilməlidir");
      return;
    }
    try {
      await api.post(`/inspections/${id}/sub-request`, {
        failed_roles: subForm.failed_roles,
        comment: subForm.comment,
      });
      toast.success("Sub-sorğu yaradıldı");
      setSubOpen(false);
      setSubForm({ failed_roles: [], comment: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  // Pre-select roles where the step is NOT_OK (default selection for sub-request)
  const defaultFailedRoles = (insp?.workflow || [])
    .filter((s) => s.status === "not_ok" && s.role !== "consulting_team_lead")
    .map((s) => s.role);

  const openSubDialog = () => {
    setSubForm({ failed_roles: defaultFailedRoles, comment: "" });
    setSubOpen(true);
  };

  const toggleSubRole = (role) => {
    setSubForm((f) => ({
      ...f,
      failed_roles: f.failed_roles.includes(role)
        ? f.failed_roles.filter((r) => r !== role)
        : [...f.failed_roles, role],
    }));
  };

  return (
    <div className="space-y-6">
      <Link
        to={`/projects/${insp.project_id}`}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" /> Layihə
      </Link>

      {/* Parent-link banner for sub-requests */}
      {insp.parent_request_id && (
        <Link
          to={`/inspections/${insp.parent_request_id}`}
          className="flex items-center justify-between gap-3 p-3 rounded-md border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors"
          data-testid="parent-link-banner"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <div className="text-sm">
              <span className="text-amber-800">Bu sub-sorğudur. Əsas yoxlanış: </span>
              <span className="font-mono font-bold text-amber-900">
                #{insp.parent_request_id.slice(0, 8)}
              </span>
            </div>
          </div>
          <span className="text-xs mono uppercase text-amber-700">
            Əsas sorğuya keç →
          </span>
        </Link>
      )}

      {/* Inspection summary header (prominent project/stage/contractor info) */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="label-overline">
                {insp.parent_request_id ? "SUB-SORĞU" : "YOXLANIŞ SORĞUSU"}
              </div>
              <span className="mono text-[10px] text-slate-400">
                #{insp.id.slice(0, 8)}
              </span>
            </div>
            <h1 className="font-heading font-black text-3xl tracking-tight">
              {insp.stage?.title ||
                (insp.stage ? t(insp.stage.stage_type) : "Mərhələ")}
            </h1>
            <div className="text-slate-600 mt-1" data-testid="insp-project-name">
              {insp.project?.name}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <StatusBadge status={insp.status} />
            {user?.role === "superadmin" && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!window.confirm(
                    "Bu yoxlanış sorğusunu birdəfəlik silmək istədiyinizə əminsinizmi? Bütün workflow tarixçəsi silinəcək."
                  )) return;
                  try {
                    await api.delete(`/inspections/${insp.id}`);
                    toast.success("Sorğu silindi");
                    nav(`/projects/${insp.project_id}`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Silmə xətası");
                  }
                }}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="delete-inspection-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Sorğunu sil
              </Button>
            )}
          </div>
        </div>
        {/* Key info grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
          <div data-testid="insp-info-stage-type">
            <div className="label-overline mb-0.5">Mərhələ tipi</div>
            <div className="text-sm font-semibold">
              {insp.stage ? t(insp.stage.stage_type) : "—"}
            </div>
          </div>
          <div data-testid="insp-info-km">
            <div className="label-overline mb-0.5">Km aralığı</div>
            <div className="text-sm font-semibold mono">
              {insp.stage?.km_start != null && insp.stage?.km_end != null
                ? `${insp.stage.km_start} — ${insp.stage.km_end} km`
                : "—"}
            </div>
          </div>
          <div data-testid="insp-info-dim">
            <div className="label-overline mb-0.5">L × W × T</div>
            <div className="text-sm font-semibold mono">
              {insp.stage?.L ?? "—"} × {insp.stage?.W ?? "—"} ×{" "}
              {insp.stage?.T ?? "—"}
            </div>
          </div>
          <div data-testid="insp-info-cost">
            <div className="label-overline mb-0.5">Smeta dəyəri</div>
            <div className="text-sm font-semibold mono">
              {insp.stage?.calc_result?.total_cost != null
                ? `${insp.stage.calc_result.total_cost.toLocaleString()} ₼`
                : "—"}
            </div>
          </div>
        </div>
        {insp.contractor && (
          <div
            className="mt-3 flex items-center gap-2 text-xs"
            data-testid="insp-info-contractor"
          >
            <span className="label-overline">Podratçı:</span>
            <span className="font-semibold">
              {insp.contractor.full_name}
              {insp.contractor.organization_name &&
                ` · ${insp.contractor.organization_name}`}
            </span>
          </div>
        )}
        {insp.stage?.payment_authorized && (
          <div
            className="mt-3 inline-flex items-center gap-1.5 text-xs mono uppercase tracking-wide bg-emerald-100 text-emerald-800 px-2 py-1 rounded"
            data-testid="insp-stage-paid"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Ödəniş icazəsi verildi
          </div>
        )}
      </div>

      {/* Stepper */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="label-overline mb-4">{t("workflow_chain")}</div>
        <WorkflowStepper workflow={insp.workflow} currentIndex={idx} />
      </div>

      {/* My Action */}
      {isMyTurn && (
        <div
          className="bg-white border-2 border-[#004D99] rounded-lg p-6 fade-in"
          data-testid="action-panel"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 bg-[#004D99] rounded-full pulse-ring" />
            <h2 className="font-heading font-bold text-xl">
              {t("waiting_for_action")}
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="label-overline">{t("upload_media")}</Label>
              <MediaUpload value={media} onChange={setMedia} folder="inspections" />
            </div>

            <div>
              <Label className="label-overline">{t("comment")} *</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                placeholder={t("comment_required")}
                data-testid="action-comment"
              />
            </div>

            {currentStep.role === "consulting_estimate_engineer" && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-4 grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">Yoxlanılan dəyər (₼)</Label>
                  <Input
                    type="number"
                    value={numericCheck.verified_cost}
                    onChange={(e) =>
                      setNumericCheck({
                        ...numericCheck,
                        verified_cost: e.target.value,
                      })
                    }
                    data-testid="numeric-cost"
                  />
                </div>
                <div>
                  <Label className="label-overline">Smeta qeydi</Label>
                  <Input
                    value={numericCheck.note}
                    onChange={(e) =>
                      setNumericCheck({ ...numericCheck, note: e.target.value })
                    }
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => submit("ok")}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 py-6"
                data-testid="action-ok-btn"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" /> {t("ok")}
              </Button>
              <Button
                onClick={() => submit("not_ok")}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white flex-1 py-6"
                data-testid="action-not-ok-btn"
              >
                <XCircle className="w-5 h-5 mr-2" /> {t("not_ok")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Team Lead create sub-request when rejected */}
      {user?.role === "consulting_team_lead" &&
        insp.team_lead_decision === "not_ok" &&
        insp.status === "rejected" && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h3 className="font-heading font-bold">
                {t("correction_required")}
              </h3>
            </div>
            <Dialog open={subOpen} onOpenChange={setSubOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#FF5722] hover:bg-[#E64A19] text-white"
                  data-testid="open-sub-request"
                  onClick={openSubDialog}
                >
                  {t("sub_request")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>{t("sub_request")}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="label-overline">
                      Geri qaytarılacaq rollar (birdən çox seçə bilərsiniz)
                    </Label>
                    <p className="text-xs text-slate-500 mb-2">
                      NOT OK işarələnmiş rollar standart olaraq seçilidir. İstədiyiniz
                      hər bir rolu işarələyə/sıxa bilərsiniz. Sub-sorğu bütün seçilmiş
                      rolları əhatə edəcək və yenidən Team Lead-ə qayıdacaq.
                    </p>
                    <div
                      className="grid grid-cols-1 gap-2"
                      data-testid="sub-roles-list"
                    >
                      {[
                        { key: "contractor", label: "Podratçı (yenidən icra)" },
                        ...WORKFLOW_CHAIN.slice(0, -1).map((r) => ({
                          key: r,
                          label: t(`role_label.${r}`),
                        })),
                      ].map((r) => {
                        const checked = subForm.failed_roles.includes(r.key);
                        const wasNotOk = (insp.workflow || []).some(
                          (s) => s.role === r.key && s.status === "not_ok",
                        );
                        return (
                          <label
                            key={r.key}
                            className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer transition-colors ${
                              checked
                                ? "border-[#FF5722] bg-orange-50"
                                : "border-slate-200 bg-white hover:bg-slate-50"
                            }`}
                            data-testid={`sub-role-${r.key}`}
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-[#FF5722]"
                              checked={checked}
                              onChange={() => toggleSubRole(r.key)}
                            />
                            <span className="text-sm flex-1">{r.label}</span>
                            {wasNotOk && (
                              <span className="text-[10px] mono uppercase tracking-wide bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                NOT OK
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="label-overline">{t("comment")}</Label>
                    <Textarea
                      value={subForm.comment}
                      onChange={(e) =>
                        setSubForm({ ...subForm, comment: e.target.value })
                      }
                      rows={4}
                      data-testid="sub-comment"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={createSubRequest}
                    className="bg-[#FF5722] hover:bg-[#E64A19]"
                    data-testid="sub-submit"
                    disabled={!subForm.failed_roles.length || !subForm.comment.trim()}
                  >
                    Yarat ({subForm.failed_roles.length} rol)
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

      {/* Workflow Details */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-heading font-bold text-lg mb-4">
          Mərhələlərin detalları
        </h3>
        <div className="space-y-4">
          {insp.workflow.map((step, i) => (
            <div
              key={i}
              className="border border-slate-200 rounded-md p-4"
              data-testid={`step-detail-${step.role}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">
                  <span className="mono text-xs text-slate-400 mr-2">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {t(`role_label.${step.role}`)}
                </div>
                <StatusBadge status={step.status} />
              </div>
              {step.assignee && (
                <div className="text-xs text-slate-500 mb-2">
                  → {step.assignee.full_name} ({step.assignee.email})
                </div>
              )}
              {step.comment && (
                <div className="text-sm bg-slate-50 p-3 rounded mt-2">
                  <div className="label-overline mb-1">{t("comment")}</div>
                  {step.comment}
                </div>
              )}
              {step.media && step.media.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
                  {step.media.map((m, mi) => (
                    <a
                      key={mi}
                      href={m.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block border border-slate-200 rounded overflow-hidden"
                    >
                      {m.type === "video" ? (
                        <video src={m.url} className="w-full h-20 object-cover" />
                      ) : (
                        <img src={m.url} alt="" className="w-full h-20 object-cover" />
                      )}
                    </a>
                  ))}
                </div>
              )}
              {step.numeric_check && (
                <div className="mt-2 text-xs mono bg-blue-50 p-2 rounded">
                  Yoxlanılan dəyər: {step.numeric_check.verified_cost} ₼
                </div>
              )}
              {step.completed_at && (
                <div className="mono text-[10px] text-slate-400 mt-2">
                  {new Date(step.completed_at).toLocaleString()}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Sub requests */}
      {insp.sub_requests && insp.sub_requests.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="font-heading font-bold text-lg mb-4">
            Sub-sorğular{" "}
            <span className="text-sm text-slate-400 font-normal">
              ({insp.sub_requests.length})
            </span>
          </h3>
          <div className="space-y-2">
            {insp.sub_requests.map((s) => {
              const subRoles = (s.workflow || [])
                .filter((w) => w.role !== "consulting_team_lead")
                .map((w) => t(`role_label.${w.role}`).split(" ")[0]);
              return (
                <Link
                  key={s.id}
                  to={`/inspections/${s.id}`}
                  className="flex justify-between items-center p-3 border border-slate-200 rounded hover:bg-slate-50 hover:border-[#004D99] transition-colors"
                  data-testid={`subrequest-link-${s.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="mono text-xs text-slate-500 shrink-0">
                      #{s.id.slice(0, 8)}
                    </div>
                    <div className="text-xs text-slate-600 truncate">
                      {subRoles.length > 0 ? subRoles.join(" → ") : "—"}
                    </div>
                    <div className="mono text-[10px] text-slate-400 shrink-0">
                      {new Date(s.created_at).toLocaleDateString("az")}
                    </div>
                  </div>
                  <StatusBadge status={s.status} />
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Inspection chat — all participants can message each other */}
      <InspectionChat inspectionId={insp.id} />
    </div>
  );
}
